import { useState, useRef, useEffect, useMemo } from 'react';
import { DropdownOption } from '../../types/shared/DropdownOption';
import { FormInputProps } from '../../types/shared/FormInputProps';

const MiniSelect: React.FC<MiniSelectProps> = ({
    name,
    disabled = false,
    value,
    options = [],
    onChange,
    scrollRef = null,
}) => {
    const node = useRef<HTMLDivElement | null>(null);

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        document.addEventListener('mousedown', handleClick);

        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    const selected = useMemo(_getSelected, [value, options]);

    let upwards = false;
    try {
        if (node.current && scrollRef?.current) {
            upwards =
                node.current.getBoundingClientRect().bottom >
                scrollRef.current.getBoundingClientRect().bottom - 180; // If field is within 180px of the bottom of the form scroll, add upwards class
        }
    } catch (e) {
        console.warn('Form select upwards ref failure:', e);
    }

    return (
        <div ref={node} onClick={() => !disabled && setIsOpen(!isOpen)}>
            <div className={`form-select mini ${disabled ? 'disabled' : ''}`}>
                <p>{selected?.label}</p>
                <i className="arrow fal fa-angle-down" />
            </div>

            {isOpen && (
                <div className={`form-select-options mini ${upwards ? 'upwards' : ''}`}>
                    <div className="options-list">
                        {options.map((opt, i) => (
                            <p
                                key={`${opt.value} - ${i}`}
                                className={`option ${value === opt.value ? 'active' : ''}`}
                                onClick={e => {
                                    e.preventDefault();
                                    handleChange(opt.value);
                                }}
                            >
                                {opt.label}
                            </p>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    function _getSelected() {
        return options.find(item => item.value === value);
    }

    function handleClick(e: MouseEvent) {
        // inside click
        if (node?.current?.contains(e.target as Node)) {
            return;
        }

        // outside click
        setIsOpen(false);
    }

    function handleChange(val: number) {
        if (value === val) return;
        onChange(name, val);
    }
};

interface MiniSelectProps extends FormInputProps<number> {
    options: DropdownOption<number>[];
    scrollRef?: React.RefObject<HTMLDivElement>;
}

export default MiniSelect;
